import { useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { clsx } from '@wonderful/wwc/dist/src/helpers/clsx';
import { TFunction, useTranslation } from 'next-i18next';
// Import Accordion
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from '@reach/accordion';
import '@reach/accordion/styles.css';

import { stripHtml } from '@helpers/utils';
import { FilterableAccordionProps, FAQItem } from './FilterableAccordion.types';

import BuilderEditingHelper from '@common/BuilderEditingHelper/BuilderEditingHelper';
import Section from '@design/Section/Section';
import Filter from '@modules/Filter/Filter';
import styles from './FilterableAccordion.module.scss';

export default function FilterableAccordion(props: FilterableAccordionProps) {
  const { isFilterEnabled, questions, faqFilterTags, builderBlock } = props;
  const componentId = builderBlock?.id;

  const { t } = useTranslation('common');
  const [activeFilter, setActiveFilter] = useState(`${t('all')}`.toLowerCase());

  const [isInViewport, setIsInViewport] = useState(false);

  const onEnterViewport = () => {
    setIsInViewport(true);
  };

  // Render builder editing helper block if questions or filter tags are empty
  if (!questions && !faqFilterTags) {
    return (
      <BuilderEditingHelper
        componentName="FilterableAccordion"
        visible={true}
      />
    );
  }

  const faqSchemaObject = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions?.map(i => ({
      '@type': 'Question',
      name: stripHtml(i.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(i.answer),
      },
    })),
  };

  const faqTags: Set<string> = new Set();

  // Go through each question item and add only unique tags to array of filterTags
  faqFilterTags?.forEach(({ tag }) => {
    if (tag) {
      faqTags.add(tag.toLowerCase());
    }
  });

  const filterTags = Array.from(faqTags);

  return (
    <>
      <Section
        className={clsx(
          'container',
          styles.accordionSection,
          isInViewport ? styles.inViewport : ''
        )}
        onEnterViewport={onEnterViewport}
        data-testid="filterable-accordion"
      >
        <div className="row">
          <div className="col-12">
            {isFilterEnabled && (
              <Filter
                tags={filterTags}
                currentFilter={activeFilter}
                componentId={componentId}
                ariaLabel="FAQ Filters"
                setActiveFilter={setActiveFilter}
                data-testid={'faq-filter'}
              />
            )}
            <Accordion
              multiple
              collapsible
              className={styles.accordionList}
              role="list"
              id={componentId}
            >
              {questions?.map((item, index) => (
                <FilteredAccordionItems
                  id={`${componentId}-${index}`}
                  key={item.question}
                  item={item}
                  activeFilter={activeFilter}
                  t={t}
                />
              ))}
            </Accordion>
          </div>
        </div>
      </Section>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(JSON.stringify(faqSchemaObject)),
        }}
      />
    </>
  );
}

function FilteredAccordionItems(props: {
  id: string;
  item: FAQItem;
  activeFilter: string;
  t: TFunction;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { id, item, activeFilter, t } = props;

  const tags: string[] = [];

  // Loop through tags and reformat each tag in the array as a string instead of object
  item?.tags?.forEach(({ tag }) => {
    if (tag) {
      tags.push(tag.toLowerCase());
    }
  });

  const isHidden =
    !tags?.includes(activeFilter) &&
    activeFilter !== `${t('all')}`.toLowerCase();

  return (
    <AccordionItem
      id={id + '-item'}
      key={item.question}
      className={clsx(
        styles.accordionListItem,
        isHidden && styles.accordionListItemHidden
      )}
      role="listitem"
      aria-hidden={isHidden}
    >
      <AccordionButton
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(item.question),
          }}
        />
        <span className={styles.plusMinus}></span>
      </AccordionButton>

      <AccordionPanel>
        <div
          tabIndex={isOpen ? 0 : -1}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }}
        />
      </AccordionPanel>
    </AccordionItem>
  );
}
